import { BRAND_FIELD_META } from '@/brand/field-meta'
import type { Brand } from '@/brand/types'
import { isRecord } from '@/lib/api'
import { describeActor } from '@/modules/audit'
import { WHITELABEL_SETTING_KEY, type SettingHistoryEntry } from '@/modules/hierarchy/api/hierarchy-api'

const ABOVE_SCOPE = 'another entity'

export type DetailRow = { property: string; before?: string; after?: string }

export type EventView = {
  entry: SettingHistoryEntry
  title: string
  inherited: boolean
  actorName: string
  location: string | null
  rows: DetailRow[]
}

export function toView(
  entry: SettingHistoryEntry,
  entityId: string,
  resolveSource: (sourceEntityId: string) => string | undefined,
  labelFor: (property: string) => string,
  formatValue: (value: unknown) => string = scalarText,
): EventView {
  const effectiveChange =
    entry.messageKey === 'activity.setting.effective-changed'
  const sourceEntity = effectiveChange
    ? entry.messageParams?.sourceEntity
    : undefined
  const inherited = sourceEntity !== undefined && sourceEntity !== entityId
  return {
    entry,
    title: eventTitle(entry),
    inherited,
    actorName: describeActor(entry.actor),
    location: inherited
      ? `Inherited from ${resolveSource(sourceEntity) ?? ABOVE_SCOPE}`
      : !effectiveChange || sourceEntity === entityId
        ? 'Changed here'
        : null,
    rows: deriveRows(entry, labelFor, formatValue),
  }
}

function eventTitle(entry: SettingHistoryEntry): string {
  if (entry.messageKey === 'activity.setting.removed') return 'Override removed'
  if (entry.messageKey === 'activity.setting.changed')
    return entry.messageParams?.old !== undefined
      ? 'Override updated'
      : 'Override set'
  return 'Effective value changed'
}

export function deriveRows(
  entry: SettingHistoryEntry,
  labelFor: (property: string) => string,
  formatValue: (value: unknown) => string = scalarText,
): DetailRow[] {
  const oldRaw = entry.messageParams?.old
  const newRaw = entry.messageParams?.new
  if (oldRaw === undefined && newRaw === undefined) return []

  const oldValue = oldRaw === undefined ? undefined : parseValue(oldRaw)
  const newValue = newRaw === undefined ? undefined : parseValue(newRaw)

  if (isRecord(oldValue) || isRecord(newValue)) {
    const before = isRecord(oldValue) ? oldValue : {}
    const after = isRecord(newValue) ? newValue : {}
    return [...new Set([...Object.keys(before), ...Object.keys(after)])]
      .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
      .map((k) => ({
        property: labelFor(k),
        before: k in before ? formatValue(before[k]) : undefined,
        after: k in after ? formatValue(after[k]) : undefined,
      }))
  }

  return [
    {
      property: 'Value',
      before: oldValue === undefined ? undefined : formatValue(oldValue),
      after: newValue === undefined ? undefined : formatValue(newValue),
    },
  ]
}

export function settingLabel(key: string): string {
  return key === WHITELABEL_SETTING_KEY
    ? 'Whitelabel branding'
    : humanizeKey(key)
}

export function propertyLabel(settingKey: string, property: string): string {
  if (settingKey === WHITELABEL_SETTING_KEY) {
    const meta = BRAND_FIELD_META[property as keyof Brand] as
      | { label: string }
      | undefined
    if (meta) return meta.label
  }
  return humanizeKey(property)
}

function humanizeKey(key: string): string {
  const words = key
    .replace(/[._-]/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function parseValue(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function scalarText(value: unknown): string {
  return typeof value === 'object' && value !== null
    ? JSON.stringify(value)
    : String(value)
}
